<template>
    <div
        v-if="!isMobile"
        class="sticky top-0 z-50 bg-white w-full h-[70px]"
    >
        <placement-header-alt
            :placement="visiblePlacementsInitials"
            :placementData="currentGroupActual"
            :index="0"
            :has-error="hasEmptySelectionErrorState"
        >
            <div v-if="hasEmptySelectionErrorState"
                class="text-red-error text-sm"
            >
                {{ $t('ERROR_NO_CUSTOMIZATION') }}
            </div>
        </placement-header-alt>
    </div>
    <div class="relative mt-[24px]">
        <step-options-front-initials
            v-if="this.customizerStore.hasPlacementOptions('CharacterStamping', 'StampingStyle')"
            :has-add-remove-button="true"
            :visible-placements-initials="visiblePlacementsInitials"
            :has-local-error-state="hasInitialsCardErrorState"
            :is-dirty="isDirty"
            :is-active="initialsIsActive"
            :scroll-to-offset="96"
            @select="handleInitialsSelect"
            @unselect="handleInitialsUnselect"
        />

        <step-options-front-stamps
            v-if="this.customizerStore.hasPlacementOptions('Stamps', 'Stamp')"
            :has-add-remove-button="true"
            :visible-placements-stamps="visiblePlacementsStamps"
            :has-local-error-state="hasStampsCardErrorState"
            :stamps-is-active="stampsIsActive"
            :is-dirty="isDirty"
            :scroll-to-offset="96"
            @select="handleStampsSelect"
            @unselect="handleStampsUnselect"
        />

        <step-options-front-paintfill
            :has-add-remove-button="true"
            :visible-placements-paintfill="visiblePlacementsPaintfill"
            :has-local-error-state="hasPaintfillCardErrorState"
            :has-paintfill-color-error-state="hasPaintfillColorErrorState"
            :is-active="paintfillIsActive"
            :is-dirty="isDirty"
            :scroll-to-offset="96"
            @select="handlePaintfillSelect"
            @unselect="openRemoveConfirmationModal"
        />

        <div class="px-[20px] lg:px-[40px]" v-if="!isAnyPaintfillSelected">
            <div class="group"
                id="step-options-front-no-customization-button"
                @click="handleSelectNoCustomization"
            >
                <div
                    :class="[
                        'w-full h-[128px] xs:h-36 bg-gray-F6F6F6 border border-b-0 cursor-pointer bg-no-repeat',
                        'group-hover:border-black group-hover:border-b-0',
                        noCustomizationSelected ? 'border-black' : (hasEmptySelectionErrorState ? 'border-red' : 'border-gray-DFDFDF'),
                    ]"
                    :style="{
                        // backgroundImage: `url(&quot;${getImage({ custom: { 'v-image': 'CP-H753_03.png' } })}&quot;)`,
                        backgroundImage: `url(&quot;${customizerStore.makeImageUrl(noCustomizationImage)}&quot;)`,
                        backgroundPosition: 'center',
                        backgroundSize: 'auto 100%',
                        backgroundRepeat: 'no-repeat',
                    }"
                >
                </div>
                <button
                    type="button"
                    :class="[
                        noCustomizationSelected ? 'border-black' : '',
                        hasEmptySelectionErrorState ? 'border-red' : noCustomizationSelected ? 'border-black' : 'border-gray-DFDFDF',
                        'border-b border-l border-r h-12 w-full flex items-center justify-between font-sans p-4 mb-[32px] text-[16px] tracking-[0.56px]',
                        'group-hover:border-black',
                        'group-focus:border-l group-focus:border-r group-focus:border-t',
                        'group-focus:outline-none group-focus:ring-1 group-focus:ring-black group-focus:border-black ring-offset-[-2px]'
                    ]"
                >
                    <div
                        class="flex w-full justify-between items-center"
                    >
                        {{ $t('NO_CUSTOMIZATION') }}
                        <black-white-button
                            class="w-[90px] rounded-full font-semibold h-[34px] flex items-center justify-center"
                            :is-selected="!noCustomizationSelected"
                        >
                            {{ noCustomizationSelected ? $t('REMOVE') : $t('ADD') }}
                        </black-white-button>
                    </div>
                </button>
            </div>
        </div>
        <teleport to="#cz3" v-if="showRemoveConfirmationModal">
            <base-modal-confirm
                @modalClose="closeRemoveConfirmationModal"
                @confirmAndExit="handlePaintfillUnselect"
                :show-modal="showRemoveConfirmationModal"
                :modal-copy="$t('RESET_PAINTFILL_CONFIRMATION_COPY')"
                :modal-heading="$t('RESET_PAINTFILL_CONFIRMATION')"
                :cta-copy="$t('REMOVE')"
                :show-cancel-button="true"
            />
        </teleport>
    </div>
</template>

<script>
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';
    import StepOptionsFrontInitials from './StepOptionsFrontInitials.vue';
    import StepOptionsFrontStamps from './StepOptionsFrontStamps.vue';
    import StepOptionsFrontPaintfill from './StepOptionsFrontPaintfill.vue';
    import BlackWhiteButton from './buttons/BlackWhiteButton.vue';
    import BaseModalConfirm from './BaseModalConfirm.vue';
    import allStores from '../stores/all';
    import getImage, { getImageSuffix } from '../helpers/getimage';

    export default {
        mixins: [allStores],

        components: {
            StepOptionsFrontStamps,
            PlacementHeaderAlt,
            StepOptionsFrontInitials,
            StepOptionsFrontPaintfill,
            BlackWhiteButton,
            BaseModalConfirm,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        emits: ['noCustomization'],

        data() {
            return {
                showRemoveConfirmationModal: false,
            };
        },

        watch: {
            hasLocalErrorState(val) {
                if (val) {
                    this.customizerStore.addPanelToErrorState('Toe Engraving');
                } else {
                    this.customizerStore.removePanelFromErrorState('Toe Engraving');
                }
            },
        },

        mounted() {
            const paintfillPlacement = this.visiblePlacementsPaintfill
                .find((placement) => placement?.code === 'PutterFrontPaintfill');
            const firstPaintfillOption = paintfillPlacement?.components
                ?.find((component) => !component.placeholder);

            if (this.isAnyPaintfillSelected && !this.paintfillIsActive && firstPaintfillOption) {
                this.customizerStore.setSelection(paintfillPlacement.code, firstPaintfillOption.code);
                this.handlePaintfillSelect({ value: firstPaintfillOption.code });
                this.customizerStore.syncPaintfillFrom(paintfillPlacement.code);
                this.customizerStore.setActiveView('Sole');
            }
        },

        computed: {
            isMobile() {
                return this.appStore?.isMobile;
            },

            isAnyPaintfillSelected() {
                return this.customizerStore.isAnyPaintfillSelected();
            },

            groupType() {
                return this.itemData?.custom?.['v-group-type'];
            },

            shouldShowColorControls() {
                return this.customizerStore.shouldShowColorControls(this.groupType);
            },

            noCustomizationSelected: {
                get() {
                    return this.customizerStore.isFrontNoCustomizationSelected('Face/Sole');
                },
                set(value) {
                    this.customizerStore.setFrontNoCustomizationSelected('Face/Sole', value);
                },
            },
            initialsIsActive() {
                return this.customizerStore.isFrontSectionActive('initials');
            },
            stampsIsActive() {
                return this.customizerStore.isFrontSectionActive('stamps');
            },
            paintfillIsActive() {
                return this.customizerStore.isFrontSectionActive('paintfill');
            },
            hasLocalErrorState() {
                return this.isDirty && !this.isComplete;
            },
            hasAnyFrontSelection() {
                return this.initialsIsActive
                    || this.stampsIsActive
                    || this.paintfillIsActive
                    || this.noCustomizationSelected;
            },
            hasEmptySelectionErrorState() {
                return this.isDirty && !this.hasAnyFrontSelection;
            },
            initialsValidationAttempted() {
                return this.isDirty
                    || this.customizerStore.hasFrontNestedPanelValidationAttempted('initials');
            },
            stampsValidationAttempted() {
                return this.isDirty
                    || this.customizerStore.hasFrontNestedPanelValidationAttempted('stamps');
            },
            paintfillValidationAttempted() {
                return this.isDirty
                    || this.customizerStore.hasFrontNestedPanelValidationAttempted('paintfill');
            },
            hasInitialsCardErrorState() {
                return this.hasEmptySelectionErrorState
                    || (this.initialsValidationAttempted && this.initialsIsActive && !this.isInitialsComplete);
            },
            hasStampsCardErrorState() {
                return this.hasEmptySelectionErrorState
                    || (this.stampsValidationAttempted && this.stampsIsActive && !this.isStampsComplete);
            },
            hasPaintfillCardErrorState() {
                return this.hasEmptySelectionErrorState
                    || (this.paintfillValidationAttempted && this.paintfillIsActive && !this.isPaintfillComplete);
            },
            hasPaintfillColorErrorState() {
                return this.paintfillValidationAttempted
                    && this.paintfillIsActive
                    && !this.isPaintfillComplete;
            },
            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            currentGroupActual() {
                return this.visibleGroups.find((group) => group.name === this.currentGroupName) || this.itemData;
            },

            currentGroupName() {
                return this.customizerStore.currentGroup;
            },

            showGroupOverlay() {
                return this.appStore.showGroupOverlay;
            },

            panelToReset() {
                return this.customizerStore.panelToReset;
            },

            isDisabled() {
                return this.primarySelectedValue === 'Stamp-Stamp-Blank';
            },

            primarySelectedValue() {
                return this.visiblePlacementsStampsNonColors?.[0]?.selectedValue;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            visiblePlacementsStamps() {
                let placements = this.customizerStore.visiblePlacements(this.itemData.subGroups.stamps) ?? [];

                const canColor = placements.some((placement) => placement.custom?.['v-allow-colors'] === 'yes');

                // Filter out colors.
                if (!canColor) {
                    placements = placements.filter((p) => {
                        if (p.custom?.['v-group-type'] === 'colors') {
                            return false;
                        }

                        return true;
                    });
                }

                return placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    // Add extra properties to each placement:
                    // - a code of the currently selected component.
                    return {
                        selectedValue: selected ? selected.code : null,
                        groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                        ...placement,
                    };
                });
            },

            visiblePlacementsStampsNonColors() {
                return this.visiblePlacementsStamps.filter((x) => !x.code.includes('Color'));
            },

            visiblePlacementsStampsColors() {
                return this.visiblePlacementsStamps.filter((x) => x.code.includes('Color'));
            },

            visiblePlacementsInitials() {
                if (this.itemData?.subGroups?.characterStamping == null) {
                    return [];
                }

                const placements = this.customizerStore.visiblePlacements(this.itemData?.subGroups?.characterStamping) ?? [];

                return placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    // Add extra properties to each placement:
                    // - a code of the currently selected component.
                    return {
                        selectedValue: selected ? selected.code : null,
                        groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                        ...placement,
                    };
                });
            },

            visiblePlacementsPaintfill() {
                const placements = [];

                const frontPaintfill = this.customizerStore.viewController.findPlacement('PutterFrontPaintfill');
                placements.push(frontPaintfill);

                return placements;
            },

            selectedInitials() {
                return this.viewController?.selectedAtPlacement('StampingStyle');
            },
            selectedStamps() {
                return this.viewController?.selectedAtPlacement('Stamp');
            },
            selectedPaintfill() {
                return this.viewController?.selectedAtPlacement('PutterFrontPaintfill');
            },
            hasInitials() {
                return !!(this.selectedInitials && !this.selectedInitials.placeholder);
            },
            hasStamps() {
                return !!(this.selectedStamps
                    && !this.selectedStamps.placeholder
                    && this.selectedStamps.code !== 'Stamp-Stamp-Blank');
            },
            hasPaintfill() {
                return this.selectedPaintfill != null && !this.selectedPaintfill.placeholder;
            },
            hasInitialsColor() {
                return this.customizerStore.uiStepSubOptions?.frontInitials
                    ?.some((option) => !!option.selectedColor);
            },
            hasStampsColor() {
                return this.customizerStore.uiStepSubOptions?.frontStamps
                    ?.some((option) => !!option.selectedColor);
            },
            hasInitialsTextError() {
                const panelErrors = this.customizerStore.panelsInErrorState ?? [];
                const currentGroup = this.customizerStore.currentGroup;

                return panelErrors.includes(`${currentGroup}-v-stamping-text-1`)
                    || panelErrors.includes(`${currentGroup}-v-stamping-text-2`)
                    || panelErrors.includes('Front-v-stamping-text-1')
                    || panelErrors.includes('Front-v-stamping-text-2');
            },
            isInitialsComplete() {
                if (!this.hasInitials) {
                    return false;
                }

                if (this.hasInitialsTextError || !this.hasInitialsColor) {
                    return false;
                }

                const requiresText1 = this.selectedInitials?.custom?.['v-stamping-has-text-1'] === 'yes';
                const text1Value = this.selectedInitials?.custom?.['v-stamping-text-1'];
                const requiresText2 = this.selectedInitials?.custom?.['v-stamping-has-text-2'] === 'yes';
                const text2Value = this.selectedInitials?.custom?.['v-stamping-text-2'];

                const text1Ok = !requiresText1 || (text1Value != null && `${text1Value}`.length > 0);
                const text2Ok = !requiresText2 || (text2Value != null && `${text2Value}`.length > 0);

                return text1Ok && text2Ok;
            },
            stampsAllowColors() {
                return this.visiblePlacementsStamps
                    .some((placement) => placement.custom?.['v-allow-colors'] === 'yes');
            },
            isStampsComplete() {
                if (!this.hasStamps) {
                    return false;
                }

                if (!this.stampsAllowColors) {
                    return true;
                }

                return this.hasStampsColor;
            },
            isPaintfillComplete() {
                return this.customizerStore.isPaintfillComplete('Face/Sole');
            },
            isComplete() {
                // Complete if:
                // - No Customization selected, OR
                // - Initials (StampingStyle) is complete, OR
                // - Stamps (Stamp) is complete, OR
                // - Paintfill (PutterFrontPaintfill) is complete

                if (this.noCustomizationSelected) {
                    return true;
                }

                if (this.hasInitials && this.hasInitialsTextError) {
                    return false;
                }

                if (this.hasPaintfill && !this.isPaintfillComplete) {
                    return false;
                }

                return this.isInitialsComplete || this.isStampsComplete || this.isPaintfillComplete;
            },
            noCustomizationImage() {
                return `PutterFace-None-${getImageSuffix(this.viewController)}`;
            },
        },

        methods: {
            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            getPlacementVariant(placement) {
                return placement?.custom?.['v-placement-variant'];
            },

            selectValue(placement, option) {
                this.customizerStore.setSelection(placement.code, option.value, false);
            },

            selectedPlaceholder(placement) {
                return placement.selectedPlaceholder;
            },

            handleSelectNoCustomization() {
                this.noCustomizationSelected = true;
                this.customizerStore.clearFrontActiveSection();
                this.customizerStore.resetCharacterStamping();
                this.customizerStore.resetStamps();
                this.customizerStore.resetPaintfill();
                this.$emit('noCustomization', true);
            },

            handleUnselectNoCustomization() {
                this.noCustomizationSelected = false;
                this.$emit('noCustomization', false);
            },

            handleInitialsSelect(value) {
                this.customizerStore.setFrontSectionActive('initials', true);
                this.handleUpdateNoCustomization(value);
            },

            handleInitialsUnselect() {
                if (this.initialsIsActive) {
                    this.customizerStore.clearFrontActiveSection('initials');
                }
            },

            handleUpdateNoCustomization(value) {
                if (value) {
                    this.noCustomizationSelected = false;
                    this.$emit('noCustomization', false);
                }
            },

            handleStampsSelect(value) {
                this.customizerStore.setFrontSectionActive('stamps', true);
                this.noCustomizationSelected = false;
                this.handleUpdateNoCustomization(value);
                this.$emit('noCustomization', false);
            },

            handlePaintfillSelect(value) {
                // this.customizerStore.setFrontSectionActive('paintfill', true);
                // this.noCustomizationSelected = false;
                this.handleUpdateNoCustomization(value);
                // this.$emit('noCustomization', false);
            },

            handleStampsUnselect() {
                if (this.stampsIsActive) {
                    this.customizerStore.clearFrontActiveSection('stamps');
                }
            },

            handlePaintfillUnselect() {
                if (this.paintfillIsActive) {
                    this.customizerStore.resetAllPaintfill();
                }

                this.closeRemoveConfirmationModal();
            },

            openRemoveConfirmationModal() {
                this.showRemoveConfirmationModal = true;
            },

            getImage(option) {
                return getImage(this.viewController, option);
            },

            closeRemoveConfirmationModal() {
                this.showRemoveConfirmationModal = false;
            },
        },
    };
</script>
